import { format } from "date-fns";
import { useCurrentApp } from "hooks/useCurrentApp";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useMemo } from "react";
import { parseDateValue } from "utils/dates";

type DateInput = Date | string | number | null | undefined;

export type FormatDateOptions = {
  withTime?: boolean;
  withSeconds?: boolean;
};

export const useFormatDate = (): {
  formatDate: (value: DateInput, options?: FormatDateOptions) => string;
  formatMonthYear: (value: DateInput) => string;
} => {
  const { t } = useI18n();
  const app = useCurrentApp();
  const dateKey =
    app === "employee" ? "apps.employee.common.date" : "apps.admin.common.date";
  const monthYearKey =
    app === "employee"
      ? "apps.employee.common.monthYear"
      : "apps.admin.common.monthYear";

  const formatDate = useCallback(
    (value: DateInput, options?: FormatDateOptions): string => {
      const date = parseDateValue(value);
      if (!date) return "";
      const formattedDate = t(dateKey, date);
      if (options?.withSeconds) {
        return `${formattedDate}, ${format(date, "HH:mm:ss")}`;
      }
      if (options?.withTime) {
        return `${formattedDate}, ${format(date, "HH:mm")}`;
      }
      return formattedDate;
    },
    [t, dateKey],
  );

  const formatMonthYear = useCallback(
    (value: DateInput): string => {
      const date = parseDateValue(value);
      if (!date) return "";
      return t(monthYearKey, date);
    },
    [t, monthYearKey],
  );

  return useMemo(
    () => ({ formatDate, formatMonthYear }),
    [formatDate, formatMonthYear],
  );
};
